import React, { type ReactElement, useCallback, useMemo, useState } from 'react';

import { EntryStatusEnum, type Penalty } from '@clubspark-node/clubspark-tennis-types';
import Button from '@mui/material/Button';
import { createColumnHelper, type Row } from '@tanstack/react-table';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { entryStatusConstants, genderConstants } from 'tods-competition-factory';

import CheckInFilledIcon from 'assets/icons/icn _ 24px _ check-in-filled.svg?react';
import Participant from 'components/participants/Participant';
import TMXCircularProgress from 'components/progress/TMXCircularProgress';
import StandardActions from 'components/tables/actions/StandardActions';
import EventChip from 'components/tables/common/EventChip';
import PlayerLink from 'components/tables/common/PlayerLink';
import { PlayerStatus } from 'components/tables/common/PlayerStatus';
import TeamsChips from 'components/tables/common/TeamsChips';
import { actionsHeaderStyles, useStyles as useTableStyles } from 'components/tables/enhancedTable/styles';
import { getSelectionColumnDefinition } from 'components/tables/enhancedTable/utils';
import { useStyles } from 'components/tables/players/styles';
import { BodyRegular } from 'components/typography/Typography';
import { emptyObject, sentenceCase } from 'components/utils';
import { Feature, featureEnabled, getClientConfig, includeIfFeatureEnabled } from 'config/clients';
import { NO_SELECTION } from 'constants/generic';
import { type ParticipantOrId } from 'containers/dialogs/ChoosePlayer';
import useScalings from 'hooks/scalings/useScalings';
import { type ParticipantInterface, PenaltyExtensionNames } from 'typedefs/store/tmxTypes';
import { formatPlayerName, getAddress } from 'utils/participant';
import { getSubScalings, type ScalingType, type SubScalingType } from 'utils/scalings';

import { SignedInStatusEnum } from './PlayersTable';

const { MALE, FEMALE } = genderConstants;

const VALID_GENDERS = ['M', 'F', MALE, FEMALE];

export const playersHiddenColumnIds = ['scaling_wtn doubles', 'firstName', 'lastName', 'city', 'state'];
export const playerColumnIds = [
  'selection',
  'name',
  'firstName',
  'lastName',
  'gender',
  'events',
  'city',
  'state',
  'teams',
  'signedIn',
  'status',
  'penalty',
  'organisationId',
  'scaling_wtn singles',
  'scaling_wtn doubles',
  'actions',
];

interface SignInProps {
  classes: any;
  rowItem: Row<ParticipantInterface>;
  onClick?: (participantId: string, signInState: SignedInStatusEnum) => Awaited<void>;
}
export const SignIn: React.FC<React.PropsWithChildren<SignInProps>> = React.memo(({ classes, rowItem, onClick }) => {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState<boolean>(false);

  const handleClick = useCallback(async () => {
    setInProgress(true);
    await onClick?.(
      rowItem.id,
      rowItem.original?.signedIn ? SignedInStatusEnum.SIGNED_OUT : SignedInStatusEnum.SIGNED_IN,
    );
    setInProgress(false);
  }, [onClick, rowItem.id, rowItem.original?.signedIn]);

  return (
    <Button
      classes={{ root: classes.signInButton }}
      className={classes.signInContainer}
      disabled={inProgress}
      size="small"
      variant="text"
      onClick={handleClick}
    >
      {inProgress ? (
        <TMXCircularProgress size={20} />
      ) : rowItem.original?.signedIn ? (
        <CheckInFilledIcon className={classes.signInFilled} />
      ) : (
        <div className={classes.signIn} />
      )}
      {!rowItem.original?.signedIn ? t('signIn') : t('signedIn')}
    </Button>
  );
});

export interface RenderPenaltiesProps {
  penalties?: Penalty[];
  className?: string;
}

export const RenderPenalties: React.FC<React.PropsWithChildren<RenderPenaltiesProps>> = ({ className, penalties }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  if (!(penalties || []).length) {
    return null;
  }

  return (
    <ul className={`${classes.penaltyList} ${className}`}>
      {penalties.map((penalty) => {
        const pointsExtension = (penalty.extensions || []).find(
          (extension) => extension.name === PenaltyExtensionNames.POINTS,
        );

        const points = pointsExtension ? t('players.penaltyPoints', { count: Number(pointsExtension.value) }) : null;

        return (
          <li key={penalty.penaltyId}>
            {penalty.penaltyCode}
            {points}
          </li>
        );
      })}
    </ul>
  );
};

export const getGendersPresent = (participants) =>
  (participants || []).reduce((genders, participant) => {
    const { person } = participant || {};
    const sex = person && person.sex && VALID_GENDERS.includes(person?.sex);
    return sex && !genders.includes(person.sex) ? genders.concat(person?.sex) : genders;
  }, []);
export const getGenderMap = (t: TFunction) => ({
  M: { text: t('genders.male'), value: MALE },
  F: { text: t('genders.female'), value: FEMALE },
  MALE: { text: t('genders.male'), value: MALE },
  FEMALE: { text: t('genders.female'), value: FEMALE },
});

export const includeIfTrue = (feature: boolean, thingToInclude: any) => {
  return feature ? [thingToInclude] : [];
};

enum PlayerDisplayStatus {
  SELECTED = 'Selected',
  ALTERNATE = 'Alternate',
  UNPARED = 'Unpaired',
  WITHDRAWN = 'Withdrawn',
}

export enum PlayerStatusOptions {
  SELECTED = 'selected',
  ALTERNATE = 'alternate',
  UNPAIRED = 'unpaired',
  WITHDRAWN = 'withdrawn',
}

const parseEventEntryStatus = (entryStatus: EntryStatusEnum): PlayerDisplayStatus => {
  if (entryStatusConstants.DIRECT_ENTRY_STATUSES.includes(entryStatus)) return PlayerDisplayStatus.SELECTED;
  else if (entryStatus === EntryStatusEnum.ALTERNATE) return PlayerDisplayStatus.ALTERNATE;
  else if (entryStatus === EntryStatusEnum.UNGROUPED || entryStatus === EntryStatusEnum.UNPAIRED)
    return PlayerDisplayStatus.UNPARED;
  else if (entryStatus === EntryStatusEnum.WITHDRAWN) return PlayerDisplayStatus.WITHDRAWN;
};

export const getStatus = (participant: ParticipantInterface): PlayerDisplayStatus[] => {
  const statuses = [];

  participant.events?.forEach((event: any) => {
    const entryStatus = parseEventEntryStatus(event.entryStatus);
    if (statuses.indexOf(entryStatus) === -1) statuses.push(entryStatus);
  });
  return statuses;
};

export const filterByStatus = (
  participant: ParticipantInterface,
  status: PlayerStatusOptions,
  selectedEventId?: string,
) => {
  const eventId = selectedEventId !== NO_SELECTION ? selectedEventId : undefined;
  if (status === PlayerStatusOptions.SELECTED) {
    return participant?.events?.some(
      (event) =>
        (event.entryStatus === EntryStatusEnum.DIRECT_ACCEPTANCE || event.entryStatus === EntryStatusEnum.CONFIRMED) &&
        (eventId ? event.eventId === eventId : true),
    );
  } else if (status === PlayerStatusOptions.ALTERNATE)
    return participant?.events?.some(
      (event) => event.entryStatus === EntryStatusEnum.ALTERNATE && (eventId ? event.eventId === eventId : true),
    );
  else if (status === PlayerStatusOptions.UNPAIRED)
    return participant?.events?.some(
      (event) =>
        (event.entryStatus === EntryStatusEnum.UNGROUPED || event.entryStatus === EntryStatusEnum.UNPAIRED) &&
        (eventId ? event.eventId === eventId : true),
    );
  else if (status === PlayerStatusOptions.WITHDRAWN)
    return participant?.events?.some(
      (event) => event.entryStatus === EntryStatusEnum.WITHDRAWN && (eventId ? event.eventId === eventId : true),
    );
  else return true;
};

export const exportPlayersToCSV = (
  participantValues: Record<string, any>,
  teams: ParticipantInterface[],
  t: TFunction,
) => {
  const profileEnabled = featureEnabled(Feature.PLAYER_PROFILES);
  const hasTeams = profileEnabled && !!teams?.length;
  const showOrganisationId = getClientConfig().showOrganisationId;
  const suspensionsEnabled = featureEnabled(Feature.SUSPENSIONS);

  return participantValues?.map((values) => ({
    Name: values.name ?? '',
    'First Name': values.firstName ?? '',
    'Last Name': values.lastName ?? '',
    Gender: sentenceCase(values.gender),
    Events: values.events ?? '',
    City: values.city ?? t('noCity'),
    State: values.state ?? t('noState'),
    ...(hasTeams && {
      Team: values.teams?.map((team) => team.participantName).join(', ') ?? '',
    }),
    'Tournament Sign In': values.signedIn === 'signed in' ? t('signedIn') : t('signIn'),
    Status: values.status?.join(', ') ?? '',
    ...(suspensionsEnabled && {
      'Suspension Points':
        (
          values.penalty?.map((penalty) => {
            const penaltyPoints = penalty?.extensions?.find((extension) => extension.name === 'points')?.value;
            return `${penalty.penaltyCode}(${penaltyPoints}pts)`;
          }) ?? []
        ).join(', ') ?? '',
    }),
    ...(showOrganisationId && {
      [`${getClientConfig().clientName} ID`]: values.organisationId ?? '',
    }),
    'WTN Singles': Number.isFinite(values.wtnSingles) ? values.wtnSingles : '-',
    'WTN Doubles': Number.isFinite(values.wtnDoubles) ? values.wtnDoubles : '-',
  }));
};

interface TableColumnsOptions {
  teams?: ParticipantInterface[];
  classes: any;
  onSignIn?: (participantId: string, signInState: SignedInStatusEnum) => Awaited<void>;
  openPlayerProfile?: (participants: ParticipantOrId[]) => Promise<void>;
  openActionsMenu?: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowItem: ParticipantInterface) => void;
  removePlayerFromTeam?: any;
  tournamentId?: string;
  selectedEventId?: string;
  showSuspensionWithdrawalWarning?: boolean;
  t: TFunction;
}
const columnHelper = createColumnHelper<ParticipantInterface>();

export const useTableColumns = ({
  teams,
  classes,
  onSignIn,
  openPlayerProfile,
  openActionsMenu,
  removePlayerFromTeam,
  tournamentId,
  selectedEventId,
  showSuspensionWithdrawalWarning,
  t,
}: TableColumnsOptions) => {
  const { classes: tableClasses } = useTableStyles();

  const getTeams = useCallback(
    (participantId: string) => {
      return (
        teams?.filter((team) => {
          return team.individualParticipantIds?.includes(participantId);
        }) ?? []
      );
    },
    [teams],
  );

  const profileEnabled = featureEnabled(Feature.PLAYER_PROFILES);
  const hasTeams = profileEnabled && !!teams?.length;
  const showOrganisationId = getClientConfig().showOrganisationId;
  const {
    availableScalingTypes,
    getValue: getScalingValue,
    getDisplayValue,
    getSortMethod,
    getDisplayName,
    getSortDirection,
  } = useScalings(emptyObject);

  return useMemo(
    () => [
      columnHelper.display(getSelectionColumnDefinition()),

      columnHelper.accessor(
        (participant: ParticipantInterface) =>
          formatPlayerName(participant.person?.standardGivenName, participant.person?.standardFamilyName),
        {
          id: 'name',
          header: t('nm'),
          cell: ({ row, getValue }) => {
            if (!profileEnabled) return <BodyRegular>{getValue()}</BodyRegular>;
            return (
              <Participant
                participantId={row.id}
                participantName={getValue()}
                showAdditionalInfo={true}
                showSuspensionWithdrawalWarning={showSuspensionWithdrawalWarning}
                onClick={() =>
                  openPlayerProfile([
                    {
                      participantId: row.original.participantId,
                      participantName: getValue(),
                      individualParticipantIds: row.original.individualParticipantIds,
                    },
                  ])
                }
              />
            );
          },
        },
      ),
      columnHelper.accessor((item) => item?.person?.standardGivenName ?? '', {
        id: 'firstName',
        header: t('fnm'),
        cell: ({ getValue, row }) => {
          if (!profileEnabled) return <BodyRegular>{getValue()}</BodyRegular>;
          return (
            <PlayerLink
              individualParticipantIds={row.original.individualParticipantIds}
              participantId={row.id}
              participantName={getValue()}
            />
          );
        },
      }),
      columnHelper.accessor((item) => item?.person?.standardFamilyName ?? '', {
        id: 'lastName',
        header: t('lnm'),
        cell: ({ getValue, row }) => {
          if (!profileEnabled) return <BodyRegular>{getValue()}</BodyRegular>;
          return (
            <PlayerLink
              individualParticipantIds={row.original.individualParticipantIds}
              participantId={row.id}
              participantName={getValue()}
            />
          );
        },
      }),
      columnHelper.accessor((item) => item?.person?.sex ?? '', {
        id: 'gender',
        header: t('gdr'),
        cell: ({ getValue }) => {
          return <BodyRegular>{sentenceCase(getValue())}</BodyRegular>;
        },
        enableGlobalFilter: false,
        filterFn: (row, columnId, filterValue) => {
          if (filterValue === NO_SELECTION) return true;
          return row.getValue('gender') === filterValue;
        },
      }),
      columnHelper.accessor((row) => row?.events?.map((event) => event.eventName).join(', '), {
        id: 'events',
        header: t('evt'),
        cell: (info) => info.row.original.events?.map((event) => <EventChip key={event.eventId} event={event} />),
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB) =>
          rowA.original.events
            .map((event) => event.eventName)
            .join('')
            .localeCompare(rowB.original.events?.map((event) => event.eventName).join('')),
        filterFn: (row, columnId, filterValue) => {
          if (filterValue === NO_SELECTION) return true;
          return row.original.events?.some((event) => event.eventId === filterValue);
        },
      }),
      columnHelper.accessor(
        (item) => (item?.person?.addresses ? (getAddress(item)?.city ?? '') : 'no city available'),
        {
          id: 'city',
          header: t('cty'),
          cell: (info) => <BodyRegular>{info.getValue()}</BodyRegular>,
          enableGlobalFilter: false,
        },
      ),
      columnHelper.accessor(
        (item) => (item?.person?.addresses ? (getAddress(item)?.state ?? '') : 'no state available'),
        {
          id: 'state',
          header: t('state'),
          cell: ({ getValue }) => <BodyRegular>{getValue()}</BodyRegular>,
          enableGlobalFilter: false,
        },
      ),
      ...includeIfTrue(
        hasTeams,
        columnHelper.accessor((item) => getTeams(item.participantId), {
          id: 'teams',
          header: t('team'),
          cell: ({ getValue, row }) => (
            <TeamsChips
              participantId={row.original.participantId}
              playerName={`${row.original.person?.standardGivenName} ${row.original.person?.standardFamilyName}`}
              teams={getValue()}
              tournamentId={tournamentId}
              onDelete={removePlayerFromTeam}
            />
          ),
          enableGlobalFilter: false,
          sortingFn: (rowA, rowB) =>
            getTeams(rowA.original.participantId)
              ?.map((team) => team.participantName)
              .join('')
              .localeCompare(
                getTeams(rowB.original.participantId)
                  ?.map((team) => team.participantName)
                  .join(''),
              ),
        }),
      ),
      columnHelper.accessor((item) => (item.signedIn ? 'signed in' : 'sign in'), {
        id: 'signedIn',
        header: t('tournamentSignIn'),
        cell: ({ row }) => <SignIn key={row.id} classes={classes} rowItem={row} onClick={onSignIn} />,
        enableGlobalFilter: false,
        filterFn: (row, columnId, filterValue) => {
          if (filterValue === NO_SELECTION) return true;
          return row.getValue('signedIn') === filterValue;
        },
      }),
      columnHelper.accessor((item) => getStatus(item), {
        id: 'status',
        header: t('stt'),
        cell: (info) => {
          return info.getValue()?.map((status: PlayerDisplayStatus) => <PlayerStatus key={status} status={status} />);
        },
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB) => getStatus(rowA.original)?.join('').localeCompare(getStatus(rowB.original)?.join('')),
        filterFn: (row, columnId, filterValue) => {
          return filterByStatus(row.original, filterValue, selectedEventId);
        },
      }),
      ...includeIfFeatureEnabled(
        Feature.SUSPENSIONS,
        columnHelper.accessor((item) => item?.penalties?.map((penalty) => penalty) ?? [], {
          id: 'penalty',
          header: t('suspensionPoints'),
          cell: ({ getValue }) => <RenderPenalties penalties={getValue()} />,
          enableGlobalFilter: false,
        }),
      ),
      ...(showOrganisationId
        ? [
            columnHelper.accessor((item) => item?.person?.personOtherIds?.[0]?.personId ?? '', {
              id: 'organisationId',
              header: t('clientId', { client: getClientConfig().clientName }),
              cell: (info) => {
                return <BodyRegular>{info.getValue()}</BodyRegular>;
              },
            }),
          ]
        : []),
      ...(availableScalingTypes
        .filter(([scalingType]) => scalingType === 'WTN')
        .flatMap<[ScalingType | SubScalingType, ScalingType]>(
          ([scalingType]) =>
            getSubScalings(scalingType)?.map((subScaling: SubScalingType): [SubScalingType, ScalingType] => [
              subScaling,
              scalingType,
            ]) || [[scalingType, scalingType]],
        )
        .map(([subScalingType, scalingType]) =>
          columnHelper.accessor((participant) => getScalingValue(scalingType, participant, subScalingType), {
            id: `scaling_${subScalingType.toLowerCase()}`,
            header: getDisplayName(scalingType, subScalingType),
            cell: ({ row }) => getDisplayValue(scalingType, row.original, subScalingType) as ReactElement,
            sortDescFirst: getSortDirection(scalingType) === 'inverted',
            invertSorting: getSortDirection(scalingType) === 'inverted',
            sortingFn: (rowA, rowB) =>
              getSortMethod(scalingType, 'TABLE_SORT', subScalingType)(rowA.original, rowB.original),
            enableGlobalFilter: false,
          }),
        ) ?? []),
      ...(openActionsMenu
        ? [
            {
              id: 'actions',
              header: t('acts'),
              cell: ({ row }) => (
                <StandardActions className={classes.actions} rowItem={row.original} onClick={openActionsMenu} />
              ),
              enableGlobalFilter: false,
              meta: {
                headerClasses: [actionsHeaderStyles().className],
                cellClasses: [tableClasses.actionsCell],
              },
              enableSortBy: false,
            },
          ]
        : []),
    ],
    [
      t,
      hasTeams,
      showOrganisationId,
      availableScalingTypes,
      openActionsMenu,
      tableClasses.actionsCell,
      profileEnabled,
      showSuspensionWithdrawalWarning,
      openPlayerProfile,
      getTeams,
      tournamentId,
      removePlayerFromTeam,
      classes,
      onSignIn,
      selectedEventId,
      getDisplayName,
      getSortDirection,
      getScalingValue,
      getDisplayValue,
      getSortMethod,
    ],
  );
};
